import { useMemo, useRef, useState, type ReactElement } from 'react';
import {
    IpxCredentialFieldClassification,
    IpxCredentialFieldKind,
    SYSTEM_DISPLAY_TIME_ZONE,
    formatTimestampForMerchantUi,
    type CredentialHistoricalRevealAvailability,
    type IpxCredentialFieldDescriptor,
    type RevealHistoricalCredentialFieldRequest,
    type RevealHistoricalCredentialFieldResponse,
    type RevealIpxCredentialSecretFieldResponse,
} from '@pillar-portal/api-types';
import { IpxCredentialFieldDisplayList } from '@/components/ipx-credentials/IpxCredentialFieldDisplayList';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';

const HistoricalCredentialRevealErrorMessage =
    'Unable to reveal this historical credential field. Try again.';

type CredentialHistoricalRevealUnavailableReason = Extract<
    CredentialHistoricalRevealAvailability,
    { readonly kind: 'UNAVAILABLE' }
>['reason'];

const HistoricalCredentialUnavailableMessages: Readonly<
    Record<CredentialHistoricalRevealUnavailableReason, string>
> = {
    LEGACY_PROTECTED_EVIDENCE:
        'Historical credential fields are unavailable for legacy audit evidence.',
    PROTECTED_EVIDENCE_UNAVAILABLE: 'Protected historical credential evidence is unavailable.',
    HISTORICAL_DEFINITION_UNAVAILABLE: 'The historical credential definition is unavailable.',
};

export interface HistoricalCredentialDisclosureProps {
    readonly availability: CredentialHistoricalRevealAvailability;
    readonly canReveal: boolean;
    readonly disclosureIdentity: string;
    readonly transactionLogReference: string;
    readonly onRevealField: (
        request: RevealHistoricalCredentialFieldRequest
    ) => Promise<RevealHistoricalCredentialFieldResponse>;
}

function HistoricalCredentialDisclosureState(
    props: HistoricalCredentialDisclosureProps
): ReactElement | null {
    const [open, setOpen] = useState(false);
    const triggerButtonRef = useRef<HTMLButtonElement>(null);
    const fieldDescriptors: readonly IpxCredentialFieldDescriptor[] = useMemo(
        () =>
            props.availability.kind === 'AVAILABLE'
                ? props.availability.fields.map((field) => ({
                      field_name: field.field_name,
                      field_kind: IpxCredentialFieldKind.TEXT,
                      field_classification: IpxCredentialFieldClassification.SECRET,
                      required: false,
                      display: { label: field.label },
                  }))
                : [],
        [props.availability]
    );

    if (!props.canReveal || props.availability.kind === 'NOT_APPLICABLE') {
        return null;
    }
    if (props.availability.kind === 'UNAVAILABLE') {
        return (
            <p className="text-sm text-muted-foreground" role="status">
                {HistoricalCredentialUnavailableMessages[props.availability.reason]}
            </p>
        );
    }

    const revealField = async (
        fieldName: string
    ): Promise<RevealIpxCredentialSecretFieldResponse> => {
        try {
            const response = await props.onRevealField({
                transaction_log_ref: props.transactionLogReference,
                field_name: fieldName,
            });
            if (
                response.transaction_log_ref !== props.transactionLogReference ||
                response.field_name !== fieldName
            ) {
                throw new Error('Historical credential response identity mismatch');
            }
            return {
                field_name: response.field_name,
                field_value: response.field_value,
            };
        } catch {
            throw new Error(HistoricalCredentialRevealErrorMessage);
        }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <Button
                ref={triggerButtonRef}
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setOpen(true)}
            >
                Reveal historical credential
            </Button>
            {open ? (
                <DialogContent
                    onCloseAutoFocus={(event) => {
                        event.preventDefault();
                        triggerButtonRef.current?.focus();
                    }}
                >
                    <DialogHeader>
                        <DialogTitle>Historical credential</DialogTitle>
                        <DialogDescription>
                            Captured{' '}
                            {formatTimestampForMerchantUi(
                                props.availability.historical_ts,
                                SYSTEM_DISPLAY_TIME_ZONE
                            )}
                            {' · '}Definition {props.availability.credential_definition_uuid}
                        </DialogDescription>
                    </DialogHeader>
                    <IpxCredentialFieldDisplayList
                        fieldDescriptors={fieldDescriptors}
                        publicFields={{}}
                        canRevealSecretFields
                        onRevealSecretField={revealField}
                    />
                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            data-action="close-historical-credential"
                            onClick={() => setOpen(false)}
                        >
                            Close
                        </Button>
                    </DialogFooter>
                </DialogContent>
            ) : null}
        </Dialog>
    );
}

export function HistoricalCredentialDisclosure(
    props: HistoricalCredentialDisclosureProps
): ReactElement | null {
    return (
        <HistoricalCredentialDisclosureState
            key={`${props.transactionLogReference}:${props.disclosureIdentity}`}
            {...props}
        />
    );
}
